import { apiClient } from '@/api/client';
import { User } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuth } from '@/lib/auth';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useNotificationMutations } from '@/pages/notifications/hooks/useNotifications';
import {
  AdminSendBroadcastDTOType,
  AdminSendNotificationDTOType,
} from '@coffice/shared/api';
import { mdiClose, mdiLoading, mdiMagnify, mdiSend } from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { createPortal } from 'react-dom';

type SendNotificationType = AdminSendNotificationDTOType;

const notificationToBroadcastType: Record<
  AdminSendNotificationDTOType,
  AdminSendBroadcastDTOType
> = {
  [AdminSendNotificationDTOType.plan_reminder]:
    AdminSendBroadcastDTOType.plan_reminder,
  [AdminSendNotificationDTOType.plan_created_by_admin]:
    AdminSendBroadcastDTOType.plan_created_by_admin,
  [AdminSendNotificationDTOType.review_reminder]:
    AdminSendBroadcastDTOType.review_reminder,
  [AdminSendNotificationDTOType.announcement]:
    AdminSendBroadcastDTOType.announcement,
  [AdminSendNotificationDTOType.marketing]: AdminSendBroadcastDTOType.marketing,
  [AdminSendNotificationDTOType.system]: AdminSendBroadcastDTOType.system,
};

interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUserId?: number;
  targetUserName?: string;
  broadcastOnly?: boolean;
}

const notificationTypes: { value: SendNotificationType; label: string }[] = [
  { value: AdminSendNotificationDTOType.announcement, label: 'Announcement' },
  { value: AdminSendNotificationDTOType.marketing, label: 'Marketing' },
  { value: AdminSendNotificationDTOType.system, label: 'System' },
  { value: AdminSendNotificationDTOType.plan_reminder, label: 'Plan Reminder' },
];

export const SendNotificationModal: React.FC<SendNotificationModalProps> = ({
  isOpen,
  onClose,
  targetUserId,
  targetUserName,
  broadcastOnly = false,
}) => {
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [type, setType] = React.useState<SendNotificationType>(
    AdminSendNotificationDTOType.announcement,
  );
  const [isBroadcast, setIsBroadcast] = React.useState(
    broadcastOnly || !targetUserId,
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const { sendNotification, broadcastNotification, isSending } =
    useNotificationMutations();
  const { addNotification } = useNotifications();
  const { isAuthenticated } = useAuth();

  const debouncedSearch = useDebounce(searchQuery, 300);

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: [queryKeys.ADMIN_USERS, debouncedSearch],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/users', {
        params: {
          query: {
            search: debouncedSearch,
            limit: 10,
          },
        },
      });
      if (response.error) throw new Error('Failed to search users');
      return (response.data || []) as User[];
    },
    enabled: isAuthenticated && debouncedSearch.length >= 2 && !isBroadcast,
  });

  React.useEffect(() => {
    if (isOpen) {
      setTitle('');
      setBody('');
      setType(AdminSendNotificationDTOType.announcement);
      setIsBroadcast(broadcastOnly || !targetUserId);
      setSearchQuery('');
      setSelectedUser(null);
      setIsDropdownOpen(false);
    }
  }, [isOpen, targetUserId, broadcastOnly]);

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setSearchQuery('');
    setIsDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !body.trim()) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Please fill in all fields',
      });
      return;
    }

    const userId = targetUserId || selectedUser?.id;

    if (!isBroadcast && !userId) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Please select a user',
      });
      return;
    }

    try {
      if (isBroadcast) {
        const result = await broadcastNotification({
          title,
          body,
          type: notificationToBroadcastType[type],
        });
        if (result.sent === 0) {
          addNotification({
            type: 'warning',
            title: 'Warning',
            message: 'No users with push notifications enabled',
          });
          return;
        }
        addNotification({
          type: 'success',
          title: 'Success',
          message: `Notification sent to ${result.sent} users`,
        });
      } else if (userId) {
        await sendNotification({ userId, title, body, type });
        addNotification({
          type: 'success',
          title: 'Success',
          message: 'Notification sent successfully',
        });
      }
      onClose();
    } catch {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to send notification',
      });
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className='fixed inset-0 z-[100] overflow-y-auto'>
      <div className='flex min-h-full items-center justify-center p-4'>
        <div
          className='fixed inset-0 bg-black/50 transition-opacity'
          onClick={onClose}
        />
        <div className='relative bg-white rounded-lg shadow-xl w-full max-w-md transform transition-all'>
          <div className='flex items-center justify-between p-4 border-b'>
            <h3 className='text-lg font-semibold text-grey-900'>
              {isBroadcast ? 'Broadcast Notification' : 'Send Notification'}
            </h3>
            <button
              onClick={onClose}
              className='text-grey-400 hover:text-grey-600'
            >
              <Icon path={mdiClose} size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className='p-4 space-y-4'>
            {targetUserId && (
              <div className='bg-blue-50 border border-blue-100 rounded-lg p-3'>
                <p className='text-sm text-blue-800'>
                  Sending to:{' '}
                  <span className='font-medium'>{targetUserName}</span>
                </p>
              </div>
            )}

            {!targetUserId && !broadcastOnly && (
              <div className='flex items-center space-x-2'>
                <input
                  type='checkbox'
                  id='broadcast'
                  checked={isBroadcast}
                  onChange={e => {
                    setIsBroadcast(e.target.checked);
                    if (e.target.checked) {
                      setSelectedUser(null);
                      setSearchQuery('');
                    }
                  }}
                  className='rounded border-grey-300 text-grey-900 focus:ring-grey-500'
                />
                <label htmlFor='broadcast' className='text-sm text-grey-700'>
                  Broadcast to all users with push notifications
                </label>
              </div>
            )}

            {!targetUserId && !broadcastOnly && !isBroadcast && (
              <div className='relative'>
                <label className='block text-sm font-medium text-grey-700 mb-1'>
                  Select User
                </label>
                {selectedUser ? (
                  <div className='flex items-center justify-between px-3 py-2 border border-grey-300 rounded-lg bg-grey-50'>
                    <span className='text-sm'>
                      {selectedUser.firstName} {selectedUser.lastName}{' '}
                      <span className='text-grey-500'>
                        (ID: {selectedUser.id})
                      </span>
                    </span>
                    <button
                      type='button'
                      onClick={() => setSelectedUser(null)}
                      className='text-grey-400 hover:text-grey-600'
                    >
                      <Icon path={mdiClose} size={16} />
                    </button>
                  </div>
                ) : (
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      {isSearching ? (
                        <Icon
                          path={mdiLoading}
                          size={18}
                          className='text-grey-400 animate-spin'
                        />
                      ) : (
                        <Icon
                          path={mdiMagnify}
                          size={18}
                          className='text-grey-400'
                        />
                      )}
                    </div>
                    <input
                      type='text'
                      value={searchQuery}
                      onChange={e => {
                        setSearchQuery(e.target.value);
                        setIsDropdownOpen(true);
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                      placeholder='Search by name...'
                      className='w-full pl-10 pr-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-500 focus:border-grey-500'
                    />
                    {isDropdownOpen &&
                      searchResults &&
                      searchResults.length > 0 && (
                        <div className='absolute z-10 w-full mt-1 bg-white border border-grey-300 rounded-lg shadow-lg max-h-48 overflow-y-auto'>
                          {searchResults.map(user => (
                            <button
                              key={user.id}
                              type='button'
                              onClick={() => handleSelectUser(user)}
                              className='w-full px-3 py-2 text-left hover:bg-grey-100 text-sm'
                            >
                              <span className='font-medium'>
                                {user.firstName} {user.lastName}
                              </span>
                              <span className='text-grey-500 ml-2'>
                                ID: {user.id}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    {isDropdownOpen &&
                      debouncedSearch.length >= 2 &&
                      searchResults?.length === 0 &&
                      !isSearching && (
                        <div className='absolute z-10 w-full mt-1 bg-white border border-grey-300 rounded-lg shadow-lg p-3 text-sm text-grey-500'>
                          No users found
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Type
              </label>
              <select
                value={type}
                onChange={e => setType(e.target.value as SendNotificationType)}
                className='w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-500 focus:border-grey-500'
              >
                {notificationTypes.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Title
              </label>
              <input
                type='text'
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder='Notification title'
                maxLength={100}
                className='w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-500 focus:border-grey-500'
              />
              <p className='text-xs text-grey-500 mt-1'>
                {title.length}/100 characters
              </p>
            </div>

            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Message
              </label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder='Notification message'
                rows={4}
                maxLength={500}
                className='w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-grey-500 focus:border-grey-500'
              />
              <p className='text-xs text-grey-500 mt-1'>
                {body.length}/500 characters
              </p>
            </div>

            <div className='flex justify-end space-x-3 pt-4'>
              <Button
                label='Cancel'
                color='light'
                onClick={onClose}
                disabled={isSending}
              />
              <Button
                label={isBroadcast ? 'Broadcast' : 'Send'}
                icon={mdiSend}
                color='primary'
                type='submit'
                loading={isSending}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalContent, document.body)
    : null;
};
